import { useMemo } from "react";
import DOMPurify from "dompurify";
import { useSlugIndex, rewriteHtmlLinks } from "@/lib/useLinkPolicy";

/**
 * Renders editor-supplied HTML safely.
 *
 * Preserves a rich set of editorial tags:
 *   block: p, h1-h6, ul, ol, li, blockquote, br, hr
 *   inline: b, strong, i, em, u, s, a, span, small, big, sub, sup, mark, code
 *
 * Strips: script, style, iframe, object, embed, on* handlers, javascript: URIs.
 *
 * Link policy applied to the sanitized HTML string (NOT post-mount DOM):
 *   - Internal `/search?q=X` links matching a published ecosystem entity
 *     are rewritten to `/ecosystem/{kind}/{slug}` (follow, same tab) —
 *     keeps internal PageRank flowing to entity fiches.
 *   - Other external links keep `target="_blank" rel="noopener noreferrer nofollow"`.
 *   - Other internal links are followable in the same tab.
 *
 * The rewrite runs inside `useMemo` against a stable HTML string so the
 * computed result flows through `dangerouslySetInnerHTML` without React
 * undoing it on subsequent re-renders.
 */

const ALLOWED_TAGS = [
  "p", "h1", "h2", "h3", "h4", "h5", "h6",
  "ul", "ol", "li", "blockquote", "br", "hr", "div",
  "b", "strong", "i", "em", "u", "s", "a", "span", "small", "big",
  "sub", "sup", "mark", "code", "pre",
];

const ALLOWED_ATTR = ["href", "target", "rel", "class", "id", "style"];

const ALLOWED_URI_REGEXP = /^(?:(?:https?|mailto|tel):|#|\/|[a-z0-9._~-]+(?:[^a-z+.\-:]|$))/i;

const HtmlText = ({ html = "", className = "", testid }) => {
  const slugIndex = useSlugIndex();

  const finalHtml = useMemo(() => {
    if (!html) return "";
    const clean = DOMPurify.sanitize(html, {
      ALLOWED_TAGS,
      ALLOWED_ATTR,
      ALLOWED_URI_REGEXP,
      FORBID_TAGS: ["script", "style", "iframe", "object", "embed", "form"],
      FORBID_ATTR: [
        "onerror", "onload", "onclick", "onmouseover", "onmouseout",
        "onfocus", "onblur", "onchange", "onsubmit",
      ],
    });
    return rewriteHtmlLinks(clean, slugIndex);
  }, [html, slugIndex]);

  if (!finalHtml) return null;
  return (
    <div
      data-testid={testid}
      className={`html-text ${className}`}
      dangerouslySetInnerHTML={{ __html: finalHtml }}
    />
  );
};

export default HtmlText;
